<template>
  <div class="vee-wrap">
    <!-- 运算符 / 字面量面板 -->
    <div class="vee-ops">
      <span class="vee-ops-label">{{ $t('运算符') }}：</span>
      <a-button
        v-for="op in panelOperators"
        :key="op.value"
        size="small"
        class="vee-op-btn"
        draggable="true"
        @dragstart="onOpDragStart(op.value)"
        @dragend="onDragEnd"
        @click="appendOp(op.value)"
        :title="op.label"
      >
        {{ op.display }}
      </a-button>

      <a-button
        size="small"
        class="vee-op-btn"
        draggable="true"
        @dragstart="onOpDragStart('()')"
        @dragend="onDragEnd"
        @click="appendOp('()')"
        :title="$t('括号（成对插入）')"
      >
        ( )
      </a-button>
      <a-button
        size="small"
        class="vee-op-btn"
        draggable="true"
        @dragstart="onOpDragStart('?:')"
        @dragend="onDragEnd"
        @click="appendOp('?:')"
        :title="$t('三目：条件 ? 真值 : 假值（成对插入）')"
      >
        ? :
      </a-button>

      <a-button
        size="small"
        class="vee-op-btn vee-bool-btn"
        draggable="true"
        @click="appendBool('true')"
        @dragstart="onBoolDragStart('true')"
        @dragend="onDragEnd"
      >
        true
      </a-button>
      <a-button
        size="small"
        class="vee-op-btn vee-bool-btn"
        draggable="true"
        @click="appendBool('false')"
        @dragstart="onBoolDragStart('false')"
        @dragend="onDragEnd"
      >
        false
      </a-button>
      <div class="vee-input-wrap">
        <div
          :draggable="numTextValid"
          @dragstart="onNumDragStart"
          @dragend="onDragEnd"
          :class="{ 'vee-op-item': numTextValid }"
        >
          <a-input-search
            v-model:value="numText"
            size="small"
            class="vee-num-input"
            :placeholder="$t('数字')"
            @pressEnter="addNumber"
          >
            <template #enterButton>
              <a-button
                size="small"
                :disabled="!numTextValid"
                @click="addNumber"
              >
                {{ $t('添加数字') }}
              </a-button>
            </template>
          </a-input-search>
        </div>
        <div
          :draggable="strTextValid"
          @dragstart="onStrDragStart"
          @dragend="onDragEnd"
          :class="{ 'vee-op-item': strTextValid }"
        >
          <a-input-search
            v-model:value="strText"
            size="small"
            class="vee-str-input"
            :placeholder="$t('字符串')"
            @pressEnter="addString"
          >
            <template #enterButton>
              <a-button
                size="small"
                :disabled="!strTextValid"
                @click="addString"
              >
                {{ $t('添加字符串') }}
              </a-button>
            </template>
          </a-input-search>
        </div>

        <!-- 时间日期立即数：switch 开启带时间(日期时间)，否则仅日期；拖拽/点击生成对应格式的字符串字面量 -->
        <div
          class="vee-date-row"
          :draggable="dateValid"
          @dragstart="onDateDragStart"
          @dragend="onDragEnd"
          :class="{ 'vee-op-item': dateValid }"
        >
          <span class="vee-date-mode-label">{{ $t('带时间') }}:</span>
          <a-switch
            v-model:checked="withTime"
            size="small"
            class="vee-date-mode"
          />

          <a-date-picker
            v-model:value="dateValue"
            :show-time="withTime"
            :format="withTime ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD'"
            size="small"
            class="vee-date-picker"
            :placeholder="$t('选择时间')"
          />
          <a-button
            size="small"
            :disabled="!dateValid"
          >
            {{ $t('添加时间') }}
          </a-button>
        </div>
      <a-button
        size="small"
        type="text"
        danger
        @click="clearExpr"
      >
        {{ $t('清空表达式') }}
      </a-button>
      </div>
    </div>

    <!-- 可复用子表达式面板：点击/拖拽把子表达式名作为 subexpr token 加入表达式 -->
    <div
      v-if="availableSubExprs.length"
      class="vee-subexpr-panel"
    >
      <span class="vee-ops-label">{{ $t('子表达式') }}：</span>
      <span
        v-for="se in availableSubExprs"
        :key="se.id"
        class="vee-subexpr-chip"
        draggable="true"
        @dragstart="onSubExprDragStart(se)"
        @dragend="onDragEnd"
        @click="appendSubExpr(se.id)"
        :title="$t('点击或拖拽引用子表达式') + '：' + se.name"
      >
        {{ se.name }}
      </span>
    </div>

    <!-- 聚合值面板（仅「聚合计算值」编辑器，且父组件未自带左侧列时）：点击/拖拽把已配置值字段作为 value token 加入表达式 -->
    <div
      v-if="availableValueFields.length && showValuePanel"
      class="vee-value-panel"
    >
      <span class="vee-ops-label">{{ $t('聚合值') }}：</span>
      <span
        v-for="vf in availableValueFields"
        :key="vf.id"
        class="vee-value-chip"
        draggable="true"
        @dragstart="onValueDragStart(vf)"
        @dragend="onDragEnd"
        @click="appendValue(vf.id)"
        :title="$t('点击或拖拽引用聚合后的值字段') + '：' + vf.label"
      >
        {{ vf.label }}
      </span>
    </div>

    <!-- 聚合计算值面板（仅「聚合计算值」编辑器，且父组件未自带左侧列时）：点击/拖拽引用已配置的其它聚合计算值（排除自身），
         作为 aggcomputed token 加入表达式，后端递归内联展开（最多 50 层防环） -->
    <div
      v-if="availableAggComputeds.length && showAggComputedPanel"
      class="vee-agg-panel"
    >
      <span class="vee-ops-label">{{ $t('聚合计算值') }}：</span>
      <span
        v-for="ac in availableAggComputeds"
        :key="ac.id"
        class="vee-agg-chip"
        draggable="true"
        @dragstart="onAggComputedDragStart(ac)"
        @dragend="onDragEnd"
        @click="appendAggComputed(ac.id)"
        :title="$t('点击或拖拽引用其它聚合计算值（后端递归展开）') + '：' + ac.label"
      >
        {{ ac.label }}
      </span>
    </div>

    <!-- 表达式画布（拖拽落点） -->
    <div
      class="vee-canvas"
      :class="{ 'drag-over': dragOverIndex >= 0 }"
      @dragover.prevent="onCanvasDragOver"
      @dragleave="onCanvasDragLeave"
      @drop.stop="onCanvasDrop"
    >
      <span
        v-if="!tokens.length"
        class="vee-canvas-placeholder"
      >
        {{ placeholder || $t('从左侧拖拽字段，或拖拽/点击运算符、数字、字符串到此处') }}
      </span>
      <span
        v-for="(t, i) in tokens"
        :key="i"
        class="vee-token"
        :class="tokenClass(t)"
        draggable="true"
        @dragstart.stop="onTokenDragStart(i)"
        @dragend="onDragEnd"
      >
        {{ tokenText(t) }}
        <close-outlined
          class="vee-token-remove"
          @click.stop="removeToken(i)"
        />
      </span>
      <!-- 插入指示线：绝对定位覆盖层，不参与 flex 布局，避免拖拽时反复重排导致抖动 -->
      <span
        v-if="dragIndicator"
        class="vee-insert-line"
        :style="{
          left: dragIndicator.left + 'px',
          top: dragIndicator.top + 'px',
          height: dragIndicator.height + 'px',
        }"
      />
    </div>

    <!-- 预览 + 校验 -->
    <div class="vee-preview-row">
      <span class="vee-preview-label">{{ $t('预览') }}：</span>
      <span
        class="vee-preview"
        :class="{ 'vee-preview-invalid': !valid }"
      >
        {{ preview || '—' }}
      </span>
      <span
        v-if="!valid"
        class="vee-warn"
      >
        {{ errorMsg || $t('表达式不完整（括号不匹配或缺少操作数）') }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref, watch, type Ref } from 'vue';
import { CloseOutlined } from '@ant-design/icons-vue';
import {
  PivotValueExprToken,
  PivotFieldOption,
  PivotSubExpression,
  PivotAggComputedRefOption,
  ExprLocalDrag,
} from '../typing';
import { validateValueExpression } from '../utils';

interface Props {
  modelValue?: PivotValueExprToken[];
  fieldOptions: PivotFieldOption[];
  placeholder?: string;
  // 当前配置中所有子表达式（供编辑器展示可拖入的子表达式名称、以及按 id 解析显示名）
  subExpressions?: PivotSubExpression[];
  // 编辑某条子表达式时，排除其自身以避免直接自引用（递归展开由后端深度上限兜底环形依赖）
  excludeSubExprId?: string;
  // 「聚合计算值」编辑器中可引用的「已配置值字段」列表（value token 引用，按 id 解析显示名）。
  // 普通值字段表达式不传此 prop，故不会渲染「聚合值」面板。
  valueFieldOptions?: { id: string; label: string }[];
  // 是否渲染内置「聚合值」面板（点击/拖拽引用已配置值字段）。
  // 当父组件（如 aggComputedValueEditor）已用独立的「左侧列」展示值字段时，应置为 false 以免重复。
  showValuePanel?: boolean;
  // 「聚合计算值」编辑器中可引用的「其它聚合计算值」列表（aggcomputed token 引用，按 id 解析显示名，
  // 并用于环形依赖校验）。普通值字段表达式不传此 prop，故不会渲染「聚合计算值」面板。
  aggComputedOptions?: PivotAggComputedRefOption[];
  // 编辑某条聚合计算值时，排除其自身以避免直接自引用与环形依赖（后端展开深度上限 50 兜底）
  excludeAggComputedId?: string;
  // 是否渲染内置「聚合计算值」面板。当父组件（如 aggComputedValueEditor）已用独立的「左侧列」
  // 展示可引用的聚合计算值时，应置为 false 以免重复。
  showAggComputedPanel?: boolean;
}
const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'update:modelValue', v: PivotValueExprToken[]): void;
  (e: 'change'): void;
}>();

// 当前可引用的子表达式列表（排除正在编辑的自身）
const availableSubExprs = computed<PivotSubExpression[]>(() => {
  const list = props.subExpressions || [];
  if (props.excludeSubExprId) {
    return list.filter(s => s.id !== props.excludeSubExprId);
  }
  return list;
});
// 子表达式 id → 名称（用于 token 展示解析）
const subExprName = (id: string): string => {
  const s = (props.subExpressions || []).find(x => x.id === id);
  return s ? s.name : id;
};

// 可引用的聚合值（已配置值字段）列表与 id → 名称解析（仅「聚合计算值」编辑器传入）
const availableValueFields = computed<{ id: string; label: string }[]>(() => props.valueFieldOptions || []);
// 是否渲染内置「聚合值」面板：父组件自带左侧列展示值字段时关闭，避免重复。
const showValuePanel = computed(() => props.showValuePanel !== false);
const valueFieldName = (id: string): string => {
  const v = (props.valueFieldOptions || []).find(x => x.id === id);
  return v ? v.label : id;
};

// 可引用的聚合计算值列表（排除正在编辑的自身，避免直接自引用；环形依赖由后端展开深度上限兜底）
const availableAggComputeds = computed<PivotAggComputedRefOption[]>(() => {
  const list = props.aggComputedOptions || [];
  if (props.excludeAggComputedId) {
    return list.filter(a => a.id !== props.excludeAggComputedId);
  }
  return list;
});
// 是否渲染内置「聚合计算值」面板：父组件自带左侧列展示聚合计算值时关闭，避免重复。
const showAggComputedPanel = computed(() => props.showAggComputedPanel !== false);
const aggComputedName = (id: string): string => {
  const a = (props.aggComputedOptions || []).find(x => x.id === id);
  return a ? a.label : id;
};

// 运算符清单：value 为线上实际符号，display 为界面展示，label 为悬停说明
const operators = [
  { value: '+', display: '+', label: '加' },
  { value: '-', display: '−', label: '减' },
  { value: '*', display: '×', label: '乘' },
  { value: '/', display: '÷', label: '除' },
  { value: '^', display: '^', label: '幂（右结合）' },
  { value: '>', display: '>', label: '大于' },
  { value: '<', display: '<', label: '小于' },
  { value: '>=', display: '≥', label: '大于等于' },
  { value: '<=', display: '≤', label: '小于等于' },
  { value: '==', display: '=', label: '等于' },
  { value: '!=', display: '≠', label: '不等于' },
  { value: '&&', display: '且', label: '逻辑与' },
  { value: '||', display: '或', label: '逻辑或' },
  { value: '!', display: '非', label: '逻辑非（一元前缀）' },
  {
    value: 'abs',
    display: 'abs',
    label: '绝对值（一元前缀，作用于其后的表达式，如 abs(x) 或 abs(x-3)）',
  },
  { value: '?', display: '?', label: '三目：条件 ? 真值 : 假值' },
  { value: ':', display: ':', label: '三目：分隔真/假值' },
];

// 面板按钮：'?' 与 ':' 由独立的「? :」三目按钮成对插入，此处过滤掉，避免单独拖入。
const panelOperators = operators.filter(o => o.value !== '?' && o.value !== ':');

const localDrag = inject<Ref<ExprLocalDrag | null>>(
  'vfLocalDrag',
  ref(null) as Ref<ExprLocalDrag | null>,
);
const dragOverIndex = ref<number>(-1);
// 插入指示线的绝对定位坐标（相对画布内容原点）；null 表示不显示。
const dragIndicator = ref<{ left: number; top: number; height: number } | null>(null);
const numText = ref('');
const strText = ref('');
// 时间日期立即数：switch 开启带时间(YYYY-MM-DD HH:mm:ss)，否则仅日期(YYYY-MM-DD)，
// 拖拽/点击生成一个对应格式的字符串字面量 token。
const withTime = ref(false);
const dateValue = ref<any>(null);
const formattedDate = computed(() => {
  if (!dateValue.value) return '';
  const fmt = withTime.value ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD';
  try {
    return dateValue.value.format(fmt);
  } catch {
    return '';
  }
});
const dateValid = computed(() => formattedDate.value !== '');

const fieldLabel = (field: string): string => {
  const f = props.fieldOptions.find(x => x.field === field);
  return f ? f.label : field;
};

// 本地权威副本：所有增删先改本地 ref 再 emit 一次。
// 不能用 computed 代理 props.modelValue——props 在同步代码内不会立即刷新，
// 连续两次 insertToken 会读到过期数组而相互覆盖（括号/三目成对插入只显示后一个 token 的根因）。
const tokens = ref<PivotValueExprToken[]>(props.modelValue ? [...props.modelValue] : []);
watch(
  () => props.modelValue,
  v => {
    tokens.value = v ? [...v] : [];
  },
  { immediate: true, deep: false },
);

const numTextValid = computed(() => {
  const s = (numText.value || '').trim();
  return s !== '' && !isNaN(Number(s));
});
const strTextValid = computed(() => (strText.value || '').trim() !== '');

// 预览与校验（校验透传聚合计算值引用选项：存在性 / 直接自引用 / 环形依赖）
const validation = computed(() =>
  validateValueExpression(tokens.value, {
    aggComputedRefs: props.aggComputedOptions,
    excludeAggComputedId: props.excludeAggComputedId,
  }),
);
const valid = computed(() => validation.value.valid);
const errorMsg = computed(() => validation.value.message || '');
const preview = computed(() =>
  tokens.value
    .map(t => {
      if (t.type === 'field') return fieldLabel(t.value);
      if (t.type === 'operator') return displayOp(t.value);
      if (t.type === 'number') return t.value;
      if (t.type === 'string') return `'${t.value}'`;
      if (t.type === 'bool') return t.value;
      if (t.type === 'lparen') return '(';
      if (t.type === 'rparen') return ')';
      if (t.type === 'subexpr') return subExprName(t.value);
      if (t.type === 'value') return valueFieldName(t.value);
      if (t.type === 'aggcomputed') return aggComputedName(t.value);
      return '';
    })
    .join(' '),
);

const displayOp = (v: string) => operators.find(o => o.value === v)?.display ?? v;

const tokenText = (t: PivotValueExprToken): string => {
  switch (t.type) {
    case 'field':
      return `${fieldLabel(t.value)}(${t.value})`;
    case 'operator':
      return displayOp(t.value);
    case 'number':
      return t.value;
    case 'string':
      return `'${t.value}'`;
    case 'bool':
      return t.value;
    case 'lparen':
      return '(';
    case 'rparen':
      return ')';
    case 'subexpr':
      return subExprName(t.value);
    case 'value':
      return `[${valueFieldName(t.value)}]`;
    case 'aggcomputed':
      return `{{${aggComputedName(t.value)}}}`;
    default:
      return '';
  }
};

const tokenClass = (t: PivotValueExprToken): string => {
  if (t.type === 'field') return 'vee-token-field';
  if (t.type === 'number') return 'vee-token-number';
  if (t.type === 'string') return 'vee-token-string';
  if (t.type === 'bool') return 'vee-token-bool';
  if (t.type === 'lparen' || t.type === 'rparen') return 'vee-token-paren';
  if (t.type === 'subexpr') return 'vee-token-subexpr';
  if (t.type === 'value') return 'vee-token-value';
  if (t.type === 'aggcomputed') return 'vee-token-aggcomputed';
  return 'vee-token-op';
};

const emitChange = () => emit('change');

// 统一提交：本地 tokens 始终更新（保证编辑体验，画布即时反映增删）；
// 但仅当表达式为「空」或「语法合法」时才向外 emit——
// 非空且语法错误的中间态不外发，从而不会触发父组件的接口调用；
// 空表达式属于合法的「无表达式/无过滤」状态，需要外发以便清除已有配置。
const commit = (arr: PivotValueExprToken[]) => {
  tokens.value = arr;
  const emitable =
    arr.length === 0 ||
    validateValueExpression(arr, {
      aggComputedRefs: props.aggComputedOptions,
      excludeAggComputedId: props.excludeAggComputedId,
    }).valid;
  if (emitable) {
    emit('update:modelValue', arr);
    emitChange();
  }
};

// ===== 拖拽起始 =====
const onOpDragStart = (op: string) => {
  localDrag.value = { kind: 'op', op };
};
const onNumDragStart = (event: DragEvent) => {
  const s = (numText.value || '').trim();
  if (s === '' || isNaN(Number(s))) {
    event.preventDefault();
    return;
  }
  localDrag.value = { kind: 'number', value: s };
};
const onStrDragStart = (event: DragEvent) => {
  const s = (strText.value || '').trim();
  if (s === '') {
    event.preventDefault();
    return;
  }
  localDrag.value = { kind: 'string', value: s };
};
const onBoolDragStart = (value: string) => {
  localDrag.value = { kind: 'bool', value };
};
const onTokenDragStart = (index: number) => {
  localDrag.value = { kind: 'token', index };
};
const onSubExprDragStart = (se: PivotSubExpression) => {
  localDrag.value = { kind: 'subexpr', id: se.id, name: se.name };
};
const onValueDragStart = (vf: { id: string; label: string }) => {
  localDrag.value = { kind: 'value', id: vf.id, name: vf.label };
};
const onAggComputedDragStart = (ac: PivotAggComputedRefOption) => {
  localDrag.value = { kind: 'aggcomputed', id: ac.id, name: ac.label };
};
const onDragEnd = () => {
  localDrag.value = null;
  dragOverIndex.value = -1;
  dragIndicator.value = null;
};

// ===== 画布落点 =====
const computeInsertIndex = (event: DragEvent): number => {
  const container = event.currentTarget as HTMLElement;
  const chips = Array.from(container.querySelectorAll('.vee-token')) as HTMLElement[];
  if (chips.length === 0) return 0;
  const y = event.clientY;
  const rects = chips.map(c => c.getBoundingClientRect());
  const rows: number[][] = [];
  rects.forEach((r, i) => {
    const ri = rows.findIndex(rowIdxs => Math.abs(rects[rowIdxs[0]].top - r.top) < 8);
    if (ri === -1) {
      rows.push([i]);
    } else {
      rows[ri].push(i);
    }
  });
  let targetRow = 0;
  let best = Infinity;
  rows.forEach((rowIdxs, ri) => {
    const r0 = rects[rowIdxs[0]];
    const dist = y < r0.top ? r0.top - y : y > r0.top + r0.height ? y - (r0.top + r0.height) : 0;
    if (dist < best) {
      best = dist;
      targetRow = ri;
    }
  });
  const rowIdxs = rows[targetRow];
  for (const i of rowIdxs) {
    const r = rects[i];
    if (event.clientX < r.left + r.width / 2) return i;
  }
  return rowIdxs[rowIdxs.length - 1] + 1;
};

// 根据插入索引，计算指示线相对画布内容原点的像素坐标。
// 用现有 chip 的 rect 推算，因指示线为绝对定位、不参与布局，故不会引起重排反馈。
const computeIndicatorPos = (
  container: HTMLElement,
  idx: number,
): { left: number; top: number; height: number } => {
  const chips = Array.from(container.querySelectorAll('.vee-token')) as HTMLElement[];
  const crect = container.getBoundingClientRect();
  // 画布内容原点：需叠加滚动量与内边距，使坐标落在内容坐标系而非可视坐标系。
  const offX = -crect.left + container.scrollLeft;
  const offY = -crect.top + container.scrollTop;
  if (chips.length === 0) {
    return { left: 10, top: 10, height: 28 };
  }
  if (idx >= chips.length) {
    const r = chips[chips.length - 1].getBoundingClientRect();
    return { left: r.right + offX + 2, top: r.top + offY, height: r.height };
  }
  const r = chips[idx].getBoundingClientRect();
  return { left: r.left + offX - 4, top: r.top + offY, height: r.height };
};

const onCanvasDragOver = (event: DragEvent) => {
  if (!localDrag.value) return;
  const container = event.currentTarget as HTMLElement;
  const idx = computeInsertIndex(event);
  dragOverIndex.value = idx;
  dragIndicator.value = computeIndicatorPos(container, idx);
};
const onCanvasDragLeave = (event: DragEvent) => {
  // dragleave 会在移动到子元素（token chip）时误触发，导致指示线闪烁。
  // 仅当指针真正离开画布外沿时才清除，避免抖动。
  const container = event.currentTarget as HTMLElement;
  const related = event.relatedTarget as Node | null;
  if (related && container.contains(related)) return;
  dragOverIndex.value = -1;
  dragIndicator.value = null;
};
const onCanvasDrop = () => {
  const d = localDrag.value;
  const insertAt = dragOverIndex.value < 0 ? tokens.value.length : dragOverIndex.value;
  dragOverIndex.value = -1;
  dragIndicator.value = null;
  if (!d) return;
  if (d.kind === 'field') {
    insertToken(insertAt, { type: 'field', value: d.field });
  } else if (d.kind === 'op') {
    if (d.op === '()') {
      // 括号成对插入：左右括号一起，光标可继续往中间填内容
      insertTokens(insertAt, [
        { type: 'lparen', value: '' },
        { type: 'rparen', value: '' },
      ]);
    } else if (d.op === '?:') {
      // 三目成对插入：问号与冒号一起
      insertTokens(insertAt, [
        { type: 'operator', value: '?' },
        { type: 'operator', value: ':' },
      ]);
    } else if (d.op === 'abs') {
      // 绝对值成对插入：abs 前缀 + 一对括号，用户往括号中间填被取绝对值的表达式
      insertTokens(insertAt, [
        { type: 'operator', value: 'abs' },
        { type: 'lparen', value: '' },
        { type: 'rparen', value: '' },
      ]);
    } else {
      insertToken(insertAt, { type: 'operator', value: d.op });
    }
  } else if (d.kind === 'number') {
    insertToken(insertAt, { type: 'number', value: d.value });
  } else if (d.kind === 'string') {
    insertToken(insertAt, { type: 'string', value: d.value });
  } else if (d.kind === 'bool') {
    insertToken(insertAt, { type: 'bool', value: d.value });
  } else if (d.kind === 'token') {
    const arr = tokens.value.slice();
    const [moved] = arr.splice(d.index, 1);
    let target = insertAt;
    if (d.index < insertAt) target -= 1;
    arr.splice(target, 0, moved);
    commit(arr);
  } else if (d.kind === 'subexpr') {
    // 引用子表达式：以 subexpr token 加入（value 为子表达式 id），后端递归内联展开
    insertToken(insertAt, { type: 'subexpr', value: d.id });
  } else if (d.kind === 'value') {
    // 聚合计算值：以 value token 加入（value 为值字段 id），后端在其聚合结果列上列式求值
    insertToken(insertAt, { type: 'value', value: d.id });
  } else if (d.kind === 'aggcomputed') {
    // 嵌套聚合计算值：以 aggcomputed token 加入（value 为聚合计算值 id），后端递归内联展开
    insertToken(insertAt, { type: 'aggcomputed', value: d.id });
  }
};

const appendSubExpr = (id: string) => {
  insertToken(tokens.value.length, { type: 'subexpr', value: id });
};
const appendValue = (id: string) => {
  insertToken(tokens.value.length, { type: 'value', value: id });
};
const appendAggComputed = (id: string) => {
  insertToken(tokens.value.length, { type: 'aggcomputed', value: id });
};

const insertTokens = (index: number, toks: PivotValueExprToken[]) => {
  const arr = tokens.value.slice();
  arr.splice(index, 0, ...toks);
  commit(arr);
};
const insertToken = (index: number, tok: PivotValueExprToken) => {
  insertTokens(index, [tok]);
};
const removeToken = (index: number) => {
  const arr = tokens.value.slice();
  arr.splice(index, 1);
  commit(arr);
};

const appendOp = (op: string) => {
  if (op === '()') {
    // 括号成对追加
    insertTokens(tokens.value.length, [
      { type: 'lparen', value: '' },
      { type: 'rparen', value: '' },
    ]);
  } else if (op === '?:') {
    // 三目问号 + 冒号成对追加
    insertTokens(tokens.value.length, [
      { type: 'operator', value: '?' },
      { type: 'operator', value: ':' },
    ]);
  } else if (op === 'abs') {
    // 绝对值：abs 前缀 + 一对括号成对追加，用户往括号中间填被取绝对值的表达式
    insertTokens(tokens.value.length, [
      { type: 'operator', value: 'abs' },
      { type: 'lparen', value: '' },
      { type: 'rparen', value: '' },
    ]);
  } else {
    insertToken(tokens.value.length, { type: 'operator', value: op });
  }
};
const addNumber = () => {
  const s = (numText.value || '').trim();
  if (s === '' || isNaN(Number(s))) return;
  insertToken(tokens.value.length, { type: 'number', value: s });
  numText.value = '';
};
const addString = () => {
  const s = (strText.value || '').trim();
  if (s === '') return;
  insertToken(tokens.value.length, { type: 'string', value: s });
  strText.value = '';
};
const onDateDragStart = (event: DragEvent) => {
  if (!dateValid.value) {
    event.preventDefault();
    return;
  }
  localDrag.value = { kind: 'string', value: formattedDate.value };
};
const addDate = () => {
  if (!dateValid.value) return;
  insertToken(tokens.value.length, { type: 'string', value: formattedDate.value });
};
const appendBool = (v: string) => {
  insertToken(tokens.value.length, { type: 'bool', value: v });
};
const clearExpr = () => {
  commit([]);
};
</script>

<style lang="less" scoped>
.vee-wrap {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.vee-ops {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  .vee-ops-label {
    font-size: 13px;
    color: #666;
  }
  .vee-op-btn {
    min-width: 32px;
    font-weight: 600;
    cursor: grab;
  }
  .vee-bool-btn {
    font-family: monospace;
  }
  .vee-num-input {
    width: 200px;
  }
  .vee-str-input {
    width: 200px;
  }
}

.vee-canvas {
  position: relative;
  min-height: 64px;
  border: 2px dashed #d9d9d9;
  border-radius: 6px;
  padding: 10px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  transition: all 0.2s;
  &.drag-over {
    border-color: #1890ff;
    background: #f0f7ff;
  }
  .vee-canvas-placeholder {
    color: #bbb;
    font-size: 12px;
  }
  .vee-insert-line {
    position: absolute;
    width: 0;
    border-left: 2px solid #1890ff;
    box-shadow: 0 0 4px rgba(24, 144, 255, 0.7);
    pointer-events: none;
    z-index: 2;
  }
  .vee-token {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 13px;
    cursor: grab;
    user-select: none;
    border: 1px solid transparent;
    &.vee-token-field {
      background: #e6f7ff;
      border-color: #91d5ff;
      color: #0958d9;
    }
    &.vee-token-op {
      background: #fafafa;
      border-color: #d9d9d9;
      font-weight: 700;
    }
    &.vee-token-number {
      background: #f6ffed;
      border-color: #b7eb8f;
      color: #389e0d;
    }
    &.vee-token-string {
      background: #fff0f6;
      border-color: #ffadd2;
      color: #c41d7f;
    }
    &.vee-token-bool {
      background: #f9f0ff;
      border-color: #d3adf7;
      color: #722ed1;
      font-family: monospace;
    }
    &.vee-token-paren {
      background: #fff7e6;
      border-color: #ffd591;
      font-weight: 700;
    }
    .vee-token-remove {
      color: #999;
      font-size: 10px;
      cursor: pointer;
      &:hover {
        color: #ff4d4f;
      }
    }
  }
}

.vee-preview-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 13px;
  .vee-preview-label {
    color: #666;
  }
  .vee-preview {
    font-weight: 600;
    color: #333;
    background: #f5f5f5;
    padding: 2px 8px;
    border-radius: 4px;
    &.vee-preview-invalid {
      color: #cf1322;
      background: #fff1f0;
    }
  }
  .vee-warn {
    color: #fa8c16;
    font-size: 12px;
  }
}
.vee-input-wrap {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  flex-direction: row;
  flex-wrap: wrap;
  width: 100%;
}

.vee-subexpr-panel {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  .vee-subexpr-chip {
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 12px;
    background: #f9f0ff;
    border: 1px solid #d3adf7;
    color: #722ed1;
    cursor: grab;
    user-select: none;
    &:hover {
      border-color: #9254de;
      background: #efdbff;
    }
    &:active {
      cursor: grabbing;
    }
  }
}
.vee-token-subexpr {
  background: #f9f0ff;
  border-color: #d3adf7;
  color: #722ed1;
}

.vee-value-panel {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  .vee-value-chip {
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 12px;
    background: #e6fffb;
    border: 1px solid #87e8de;
    color: #08979c;
    cursor: grab;
    user-select: none;
    &:hover {
      border-color: #13c2c2;
      background: #b5f5ec;
    }
    &:active {
      cursor: grabbing;
    }
  }
}
.vee-token-value {
  background: #e6fffb;
  border-color: #87e8de;
  color: #08979c;
}

// 「聚合计算值」面板与 token：geekblue 色系，与「聚合值」(cyan)、「子表达式」(purple) 区分
.vee-agg-panel {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  .vee-agg-chip {
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 12px;
    background: #f0f5ff;
    border: 1px solid #adc6ff;
    color: #2f54eb;
    cursor: grab;
    user-select: none;
    &:hover {
      border-color: #85a5ff;
      background: #d6e4ff;
    }
    &:active {
      cursor: grabbing;
    }
  }
}
.vee-token-aggcomputed {
  background: #f0f5ff;
  border-color: #adc6ff;
  color: #2f54eb;
}

.vee-date-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: nowrap;
  .vee-date-mode {
    flex-shrink: 0;
  }
  .vee-date-mode-label {
    font-size: 13px;
    color: #666;
    flex-shrink: 0;
  }
  .vee-date-picker {
    width: 220px;
  }
}
.vee-op-item {
  cursor: grab;
}
</style>
